import { AppTypeEnum } from '@shared/app/types';
import type { StoreApi } from 'zustand';
import { Logger } from '../../shared/services/logger.service';

interface LoadedApp {
  id: string;
  type: AppTypeEnum;
  controller?: any;
  element?: HTMLElement;
}

interface AppConfig {
  type: AppTypeEnum;
  url?: string;
}

class GlobalAppController {
  private apps: Record<string, LoadedApp> = {};
  private appStores: Record<string, StoreApi<any>> = {};
  private readyResolve!: () => void;
  private readyPromise!: Promise<void>;

  private coreApps = [
    'marketplace',
    'meeting',
    'office',
    'chat',
    'rocketchat',
    'profile',
    'nexgenCX',
    'calendar',
    'reception',
    'recording',
    'webexMeet',
    'reports',
    'administration',
    'webexChats',
    'notes',
    'breakoutSession',
    'whiteboard',
    'platformAdmin',
    'referaclient',
    'training',
  ];
  private remoteApps: string[] = ['sample', 'bookmarks'];
  private localAppModules = import.meta.glob('/src/apps/*/AppController.ts');

  constructor() {
    let resolveFn!: () => void;
    this.readyPromise = new Promise((resolve) => {
      this.readyResolve = resolve;
    });

    this.initialize();
  }

  /** Register a Zustand store for external access */
  registerAppStore<T>(appId: string, store: StoreApi<T>) {
    this.appStores[appId] = store;
  }

  /** Access a typed Zustand store by app ID */
  getAppStore<T = any>(appId: string): StoreApi<T> | undefined {
    return this.appStores[appId];
  }

  /** Core boot logic */
  private async initialize() {
    try {
      await Promise.all([this.loadCoreApps(), this.loadRemoteApps()]);
      Logger.debug('All apps loaded successfully.');
    } catch (error) {
      Logger.error('Error initializing apps:', error);
    } finally {
      this.readyResolve();
    }
  }

  public async waitForInitialization(): Promise<void> {
    return this.readyPromise;
  }

  private async loadCoreApps() {
    for (const appId of this.coreApps) {
      await this.lazyLoadApp(appId);
    }
  }

  private async loadRemoteApps() {
    for (const appId of this.remoteApps) {
      await this.lazyLoadApp(appId);
    }
  }

  async lazyLoadApp(appId: string) {
    if (this.apps[appId]) {
      Logger.warn(`App ${appId} is already loaded.`);
      return;
    }

    try {
      const appConfig = await this.getAppConfig(appId);

      Logger.debug('App configuration:', appConfig);

      if (!appConfig) {
        Logger.error(`No configuration found for app: ${appId}`);
        return;
      }

      if (appConfig.type === 'local') {
        const appPath = `/src/apps/${appId}/AppController.ts`;
        if (!this.localAppModules[appPath]) {
          Logger.error(`App ${appId} not found at ${appPath}`);
          return;
        }

        const module = (await this.localAppModules[appPath]()) as { AppSDK: any };
        const AppSDK = module.AppSDK;
        this.registerApp(appId, 'local', AppSDK);
        if (typeof AppSDK.onAppReady === 'function') {
          AppSDK.onAppReady();
        }
      } else if (appConfig.type === 'iframe') {
        const iframe = document.createElement('iframe');
        iframe.src = <string>appConfig.url;
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        document.body.appendChild(iframe);
        this.registerApp(appId, 'iframe', undefined, iframe);
      }
    } catch (error) {
      Logger.error(`Failed to load app: ${appId}`, error);
    }
  }

  async getAppConfig(appId: string): Promise<AppConfig | null> {
    const config: { [key: string]: AppConfig } = {
      marketplace: { type: 'local' },
      sample: { type: 'local' },
      bookmarks: { type: 'local' },
      meeting: { type: 'local' },
      office: { type: 'local' },
      chat: { type: 'local' },
      rocketchat: { type: 'local' },
      profile: { type: 'local' },
      nexgenCX: { type: 'local' },
      calendar: { type: 'local' },
      analytics: { type: 'iframe', url: 'https://analytics.example.com' },
      reception: { type: 'local' },
      recording: { type: 'local' },
      webexMeet: { type: 'local' },
      reports: { type: 'local' },
      administration: { type: 'local' },
      webexChats: { type: 'local' },
      notes: { type: 'local' },
      breakoutSession: { type: 'local' },
      auditorium: { type: 'local' },
      whiteboard: { type: 'local' },
      platformAdmin: { type: 'local' },
      referaclient: { type: 'local' },
      training: { type: 'local' },
    };
    return config[appId] || null;
  }

  private registerApp(appId: string, type: AppTypeEnum, controller?: any, element?: HTMLElement) {
    this.apps[appId] = { id: appId, type, controller, element };
  }

  getLoadedApps() {
    return Object.keys(this.apps);
  }

  getAllApps() {
    return Object.values(this.apps);
  }

  mountView(appId: string, panel: string, viewName: string) {
    const app = this.apps[appId];
    if (app?.type === 'module' && app.controller?.mountView) {
      app.controller.mountView(panel as any, viewName);
    }
  }

  unmountView(appId: string, panel: string, viewId: string) {
    const app = this.apps[appId];
    if (app?.type === 'module' && app.controller?.unmountView) {
      app.controller.unmountView(panel as any, viewId);
    }
  }

  getManifest(appId: string) {
    return this.apps[appId]?.controller?.manifest ?? null;
  }

  getAvailableViews(appId: string) {
    return this.apps[appId]?.controller?.getAvailableViews() ?? null;
  }

  getAppComponent(appId: string, viewName: string) {
    const app = this.apps[appId];
    if (!app || !['local', 'module'].includes(app.type)) return null;
    return app.controller?.renderView?.(viewName) ?? null;
  }
}

interface HotData {
  globalApp?: GlobalAppController;
}

const hotData = (import.meta.hot?.data as HotData) || {};

if (!hotData.globalApp) {
  hotData.globalApp = new GlobalAppController();
}

export const GlobalAppSDK = hotData.globalApp;

if (import.meta.hot) {
  import.meta.hot.accept();
}
